import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface DeleteUserModalProps {
  open: boolean;
  role: 'PLAYER' | 'CENTER';
  loading?: boolean;
  onClose: () => void;
  onConfirm: (remark: string) => void;
}

const DeleteUserModal = ({
  open,
  role,
  loading = false,
  onClose,
  onConfirm,
}: DeleteUserModalProps) => {
  const [remark, setRemark] = useState<string>('');
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      setRemark('');
    }
  }, [open]);

  if (!open) return null;

  const onChangeRemark = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setRemark(e.target.value);
  };

  const onClickConfirm = () => {
    if (!remark.trim()) {
      alert(t('common.enterDeleteReason'));
      return;
    }

    onConfirm(remark.trim());
  };

  const title = role === 'PLAYER' ? t('common.player') : t('common.center');
  return (
    <Backdrop>
      <Modal>
        <Title>{`${title} ${t('common.delete')}`}</Title>

        <Description>
          {t('common.confirmDelete', {
            target: title,
          })}
          <br />
          {t('common.enterDeleteReason')}
        </Description>

        <TextArea
          value={remark}
          onChange={onChangeRemark}
          placeholder={t('common.enterDeleteReason')}
          maxLength={300}
        />

        <Count>{remark.length}/300</Count>

        <ButtonArea>
          <CancelButton onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </CancelButton>

          <DeleteButton onClick={onClickConfirm} disabled={loading}>
            {t('common.delete')}
          </DeleteButton>
        </ButtonArea>
      </Modal>
    </Backdrop>
  );
};

export default DeleteUserModal;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);

  display: flex;
  justify-content: center;
  align-items: center;

  z-index: 9999;
`;

const Modal = styled.div`
  width: 480px;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-sizing: border-box;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
`;

const Description = styled.p`
  margin: 16px 0;
  color: #666;
  line-height: 1.5;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  resize: none;

  padding: 12px;
  box-sizing: border-box;

  border: 1px solid #ddd;
  border-radius: 8px;

  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #3e63dd;
  }
`;

const Count = styled.div`
  margin-top: 8px;
  text-align: right;
  color: #999;
  font-size: 12px;
`;

const ButtonArea = styled.div`
  margin-top: 24px;

  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button`
  width: 96px;
  height: 40px;

  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
`;

const CancelButton = styled(Button)`
  background: white;
  border: 1px solid #ddd;
`;

const DeleteButton = styled(Button)`
  background: #e53935;
  color: white;
  border: none;
`;
